import { Dialog, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React from 'react';
import { Copy, X } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { Button } from '../../global/forms/Button';
import { Input } from '../../global/forms/Input';

const useReportDuplicatorClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: 448,
        gap: 16,
        padding: '40px 40px 24px 40px',
        color: theme.colors.neutral[1],
        '& > span:first-child': { ...theme.fonts.h4 },
        '& > span:nth-child(2)': { ...theme.fonts.label },
    },
    buttons: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 16,
    },
});

/**
 * Dialog to duplicate a report.
 * This component does not duplicate reports on the backend by its own, it only informs actions to parent components.
 *
 * @param props.loading indicates a loading state for both loading the report data or the delete action
 * @param props.title report title
 * @param props.open set the dialog display state
 * @param props.onClose callback triggered when the user clicks on the background, if not provided, onCancel is called
 * @param props.onCancel callback triggered when the user clicks on the cancel button
 * @param props.onDuplicate callback triggered when the user clicks on the duplicate button
 */
export const DuplicateReport = (props: {
    loading?: boolean;
    title: string;
    open: boolean;
    onClose?: () => void;
    onCancel?: () => void;
    onDuplicate: (name: string) => void;
}) => {
    const classes = useReportDuplicatorClasses();
    const { t } = usePrefixTranslation('report', { report: 'dialogs.DuplicateReport' });
    const [title, setTitle] = React.useState(`${props.title} - copy`);

    if (props.loading)
        return (
            <Dialog classes={{ paper: classes.root }} open={props.open} onClose={props.onClose ?? props.onCancel}>
                <Skeleton variant='text' width='50%' />
                <Skeleton variant='text' width='70%' />
                <Skeleton variant='rect' height='78px' />
                <div className={classes.buttons}>
                    <Skeleton variant='rect' width='180px' height='50px' />
                    <Skeleton variant='rect' width='180px' height='50px' />
                </div>
            </Dialog>
        );

    return (
        <Dialog classes={{ paper: classes.root }} open={props.open} onClose={props.onClose ?? props.onCancel}>
            <span>{t('title')}</span>
            <span>{t('subtitle')}</span>
            <Input theme='primary' label={t('label-title')} value={title} onChange={e => setTitle(e.target.value)} />
            <div className={classes.buttons}>
                <Button theme='light' onClick={props.onCancel}>
                    <X /> {t('button-cancel')}
                </Button>
                <Button theme='primary' onClick={() => props.onDuplicate(title)}>
                    <Copy /> {t('button-duplicate')}
                </Button>
            </div>
        </Dialog>
    );
};
